// **Third Party Library
import Anser from 'anser'
import DOMPurify from 'dompurify'
import { jwtDecode } from 'jwt-decode'
import moment from 'moment'
import 'moment-timezone'
import { toast } from 'react-toastify'
import { format } from 'sql-formatter'
import Swal from 'sweetalert2'

// **Constants
import Icon from '../components/icon'
import { ENVIRONMENT } from '../env/env'
import {
  AI_SOLUTION_ICONS,
  APP_MODE,
  APP_MODES_DATA,
  ASSESSMENT_STAGE_STATUS,
  CHALLENGE_TYPES,
  currencyLocaleMap,
  currencyMap,
  DOMAIN,
  EDITOR_DEFAULT_LANGUAGE_FILES,
  ERROR_500,
  ROLES,
  SORT_TYPE,
  stageStatus
} from './constant'
import { TOOLTIPS } from './tooltips'

export const convertAnsiToHtml = (text) => {
  return Anser.ansiToHtml(text)
}

/**
 *
 * @param {string} name
 * @param {string} value
 * @param {object} options
 */

export const setCookie = (name, value, options = {}) => {
  let expires = ''

  if (options.maxAge) {
    const now = new Date()
    now.setTime(now.getTime() + options.maxAge * 60 * 1000)
    expires = `; expires=${now.toUTCString()}`
  }

  const path = options.path ? `; path=${options.path}` : ''
  const domain = options.domain ? `; domain=${options.domain}` : ''

  const secure = options.secure ? '; secure' : ''

  const sameSite = options.sameSite ? `; SameSite=${options.sameSite}` : ''

  document.cookie = `${name}=${value}${expires}${path}${domain}${secure}${sameSite}`
}

/**
 *
 * @param {string} name
 * @returns
 */
export const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

/**
 *
 * @param {string} name
 */
export const deleteCookie = (name, domain = null) => {
  // Set the cookie's expiration date to a past date
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${
    domain ? `domain=${domain}` : ''
  }`
}

export const handleRedirectPathSession = (value = null, getValue = false) => {
  if (getValue) {
    return sessionStorage.getItem('redirect') || null
  }
  if (value !== null) {
    sessionStorage.setItem('redirect', value)
  }
}

export const clearLocalAndSessionStorage = () => {
  localStorage.clear()
  sessionStorage.clear()
}

/**
 * Logout user
 */
export const logoutUser = () => {
  localStorage.clear()
  sessionStorage.clear()
  window.location.reload()
}

/**
 *
 */
export const getAudioDeviceId = () => {
  let localAudioId = localStorage.getItem('selected-audio-input')
  const cookieAudioId = getCookie('selected-audio-input')

  if (!localAudioId && cookieAudioId) {
    localAudioId = cookieAudioId
    localStorage.setItem('selected-audio-input', cookieAudioId)
  }

  return localAudioId
    ? {
        deviceId: localAudioId?.split('__')?.[0],
        groupId: localAudioId?.split('__')?.[1]
      }
    : null
}

export const getOutputAudioDeviceId = () => {
  let localAudioId = localStorage.getItem('prerequisite-audio-output-deviceId')
  const cookieAudioId = getCookie('prerequisite-audio-output-deviceId')

  if (!localAudioId && cookieAudioId) {
    localAudioId = cookieAudioId
    localStorage.setItem('prerequisite-audio-output-deviceId', cookieAudioId)
  }

  return localAudioId || null
}

/**
 *
 * @param {string} deviceId
 */
export const setAudioDeviceId = (deviceId, groupId) => {
  const storedValue = `${deviceId}__${groupId}`
  localStorage.setItem('selected-audio-input', storedValue)
  setCookie('selected-audio-input', storedValue, {
    path: '/',
    ...(window?.location?.hostname !== 'localhost'
      ? {
          domain: `.${DOMAIN}`,
          secure: window.location.origin.startsWith('https'),
          sameSite: 'Lax'
        }
      : {}),
    maxAge: 12 * 30 * 24 * 60
  })
}

export const setOutputAudioDeviceId = (deviceId) => {
  localStorage.setItem('prerequisite-audio-output-deviceId', deviceId || '')
  setCookie('prerequisite-audio-output-deviceId', deviceId || '', {
    path: '/',
    ...(window?.location?.hostname !== 'localhost'
      ? {
          domain: `.${DOMAIN}`,
          secure: window.location.origin.startsWith('https'),
          sameSite: 'Lax'
        }
      : {}),
    maxAge: 12 * 30 * 24 * 60
  })
}

/**
 *
 * @returns Return the APP Mode (Learner || Hiring)
 */
export const getAppMode = () => {
  const localMode = localStorage.getItem('app-mode')?.trim()
  return localMode === '' || localMode === null || localMode === undefined
    ? APP_MODE.HIRING
    : APP_MODES_DATA?.find((mode) => mode.value === localMode)?.value ||
        APP_MODE.HIRING
}

/**
 *
 * @param {string} appMode
 */
export const setAppModeInStorage = (appMode) => {
  localStorage.setItem('app-mode', appMode)
}

// Public Token Utils
export const getPublicToken = () => {
  return sessionStorage.getItem('public-token')
}

export const setPublicToken = (token) => {
  sessionStorage.setItem('public-token', token)
}

export const removePublicToken = () => {
  sessionStorage.removeItem('public-token')
}

export const checkPublicRoute = () => {
  return window.location.pathname.startsWith('/share/')
}

/**
 * For Getting the JWT Token from Local Storage
 * */
export const getJWTToken = () => {
  if (checkPublicRoute()) {
    const publicToken = getPublicToken()
    if (publicToken) {
      return publicToken
    } else {
      return null
    }
  }

  let token
  const rememberMe = getRememberMeFlag()

  if (rememberMe) {
    token = localStorage.getItem('user-token')
  } else {
    token = sessionStorage.getItem('user-token')
    const activeSession = getCookie('activeSession')

    if (activeSession && !token) {
      token = localStorage.getItem('user-token')
      sessionStorage.setItem('user-token', token)
    }
  }

  return !token || token === 'undefined' || token === 'null' ? undefined : token
}

/**
 * For Setting the JWT Token to Local Storage
 * */
export const setJWTToken = (token) => {
  const rememberMe = getRememberMeFlag()

  localStorage.setItem('user-token', token)
  if (!rememberMe) {
    sessionStorage.setItem('user-token', token)
    setCookie('activeSession', true, {
      path: '/',
      domain: `.${DOMAIN}`,
      secure: window.location.origin.startsWith('https'),
      sameSite: 'Lax',
      maxAge: 1140
    })
  }
}

/**
 * For removing the JWT Token from Local Storage
 * */
export const removeJWTToken = () => {
  removeRememberMeFlag()
  localStorage.removeItem('user-token')
  sessionStorage.removeItem('user-token')
  deleteCookie('activeSession')
}

/**
 * For getting the remember me flag from local storage
 */
export const setRememberMeFlag = (value) => {
  const stringifiedValue = JSON.stringify(value)
  localStorage.setItem('remember-me', stringifiedValue)
}

/**
 * For setting the remember me flag to local storage
 */
export const getRememberMeFlag = () => {
  const flag = localStorage.getItem('remember-me')

  return flag !== null ? JSON.parse(flag) : undefined
}

/**
 * For removing the remember me flag  from Local Storage
 * */
export const removeRememberMeFlag = () => {
  localStorage.removeItem('remember-me')
}

/**
 * For removing the JWT Token from Local Storage
 * */
export const getUserDetails = () => {
  const jwt = getJWTToken()
  let workspaceId = getUserWorkspaceId()

  if (jwt) {
    try {
      const details = jwtDecode(jwt)

      if (!workspaceId && Object.keys(details?.workspaces || {})?.length > 0) {
        const value = Object.keys(details?.workspaces || {})?.[0]
        setUserWorkspaceId(value)
        workspaceId = value
      }

      return {
        ...details,
        roles: details?.isSuperAdmin
          ? 'ADMIN'
          : details?.isCandidate
            ? 'CANDIDATE'
            : details?.isDeveloper
              ? 'DEVELOPER'
              : workspaceId
                ? details?.workspaces?.[workspaceId] || null
                : null
      }
    } catch (e) {
      removeJWTToken()
      return null
    }
  } else {
    return null
  }
}

/**
 *
 * @returns {String || null} user role else null
 */
export const getUserRole = () => {
  const userData = getUserDetails()
  return userData?.roles || null
}

/**
 *
 * @param {object} allowedRoles
 * @returns true if user has role else false
 */
export const checkUserAccess = (allowedRoles, allowAdmin = true) => {
  const currentRole = getUserRole()?.toLowerCase()
  if (currentRole === ROLES.CANDIDATE) {
    return false
  } else if (currentRole === ROLES.ADMIN && allowAdmin) {
    return true
  } else if (allowedRoles.includes(currentRole)) {
    return true
  }
  return false
}

export const checkAppModeAccess = (allowedMode = [], currentMode) => {
  return allowedMode?.includes(currentMode)
}

/**
 *
 * @param {String} role
 * @returns
 */
export const checkCurrentRole = (role) => {
  const currentRole = getUserRole()?.toLowerCase()
  return role === currentRole
}

/**
 *
 * @param {array} unallowedRoles
 * @returns boolean check if current role is in unallowed list
 */
export const isRoleExcluded = (unallowedRoles) => {
  const currentRole = getUserRole()?.toLowerCase()

  if (!currentRole) {
    return true
  }
  if (unallowedRoles?.includes(currentRole)) {
    return true
  }
  return false
}

/**
 *
 * @returns true if user has dev role else false
 */
export const checkDeveloperAccess = () => {
  const currentRole = getUserRole()?.toLowerCase()
  if (currentRole === ROLES.DEVELOPER) {
    return true
  }

  return false
}

/**
 *
 * @returns true if user has role else false
 */
export const checkCandidateAccess = () => {
  const currentRole = getUserRole()?.toLowerCase()
  if (currentRole === ROLES.CANDIDATE) {
    return true
  }
  return false
}

/**
 * For converting hex to rgba color
 * */
export const hexToRGBA = (hexCode, opacity, needValues = false) => {
  let hex = hexCode.replace('#', '')
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return needValues ? [r, g, b, opacity] : `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export function rgbaToHexWithoutOpacity(
  r,
  g,
  b,
  a,
  backgroundColor = { r: 255, g: 255, b: 255 }
) {
  // Ensure RGB values are within 0-255 and alpha is within 0-1
  r = Math.max(0, Math.min(255, r))
  g = Math.max(0, Math.min(255, g))
  b = Math.max(0, Math.min(255, b))
  a = Math.max(0, Math.min(1, a))

  // Calculate the blended RGB values against the background color
  let blendedR = Math.round((1 - a) * backgroundColor.r + a * r)
  let blendedG = Math.round((1 - a) * backgroundColor.g + a * g)
  let blendedB = Math.round((1 - a) * backgroundColor.b + a * b)

  // Convert blended RGB to hex
  let rHex = blendedR.toString(16).padStart(2, '0')
  let gHex = blendedG.toString(16).padStart(2, '0')
  let bHex = blendedB.toString(16).padStart(2, '0')

  return `#${rHex}${gHex}${bHex}`
}

/**
 * For converting hex to rgba color
 * */
export const hexToRGB = (hexCode, needValues = false) => {
  let hex = hexCode.replace('#', '')
  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
  }
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return needValues ? [r, g, b] : `rgba(${r}, ${g}, ${b})`
}

/**
 *
 * @param {String} mode user mode preference (light or dark)
 */
export const setUserModePreference = (mode) => {
  localStorage.setItem('mode-preference', mode)
}

/**
 * Get User Mode Peference from Local Storage
 */
export const getUserModePreference = () => {
  const mode = localStorage.getItem('mode-preference')
  return mode || ''
}

/**
 * SET User Workspace into Local Storage
 */
export const setUserWorkspace = (workspace) => {
  localStorage.setItem('workspace', JSON.stringify(workspace))
}

/**
 * Get User Workspace from Local Storage
 */
export const getUserWorkspace = () => {
  try {
    const workspace = JSON.parse(localStorage.getItem('workspace') || null)
    return workspace
  } catch (e) {
    return null
  }
}

/**
 * Remove User Workspace from Local Storage
 */
export const removeUserWorkspace = () => {
  localStorage.removeItem('workspace')
}

/**
 * SET User Workspace into Local Storage
 */
export const setUserWorkspaceId = (workspace) => {
  if (workspace) {
    localStorage.setItem('workspaceId', workspace)
  } else {
    localStorage.removeItem('workspaceId')
  }
}

/**
 * Get User Workspace from Local Storage
 */
export const getUserWorkspaceId = () => {
  return localStorage.getItem('workspaceId')
}

/**
 * Remove User Workspace from Local Storage
 */
export const removeUserWorkspaceId = () => {
  localStorage.removeItem('workspaceId')
}

/**
 *
 * @param {String} string User Name
 * @returns Returns User Name Intials (eg John Doe => JD)
 */
export const getInitials = (string) => {
  return (
    string?.split(/\s/).reduce((response, word, index) => {
      if (index <= 1) {
        response += word.slice(0, 1)
      }
      return response?.toUpperCase()
    }, '') || <Icon icon={'mdi:person'} fontSize={'1.2rem'} />
  )
}

/**
 *
 * @param {object} languageData
 * @returns format language data
 */
export const formatLanguageData = (languageData) => {
  const data =
    languageData?.length > 0 &&
    languageData?.reduce((acc, obj) => {
      return {
        ...acc,
        [obj?.language]: {
          ...obj,
          runtimes:
            obj?.runtimes?.map((runTime) => ({
              languageRuntime: runTime?.runtime,
              languageRuntimeLabel: runTime?.label
            })) || []
        }
      }
    }, {})
  return data
}

/**
 *
 * @param {string} pathname
 * @returns true or false
 */
export const findMatchedRoute = (pathname) => {
  // Your route definitions from RouterComponent
  const routes = []

  // Iterate over routes to find a match
  for (const route of routes) {
    if (route.path === pathname) {
      return route // Exact match found
    } else if (route.path && route.path.includes(':')) {
      // Handle dynamic paths
      const pattern = new RegExp(
        '^' + route.path.replace(/:[^\s/]+/g, '([\\w-]+)') + '$'
      )
      if (pattern.test(pathname)) {
        return route // Match found for dynamic path
      }
    }
  }

  return null // No match found
}

/**
 *
 * @param {object} paths
 * @returns tree like structure represting folder and files
 */
export function buildFolderAndFilesStructure(paths) {
  const tree = {}
  paths.forEach((path) => {
    if (path?.path) {
      const parts = path?.path.split('/')
      let currentLevel = tree
      let parentPath = '' // Initialize parent path for the root level

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]
        const isFolder =
          i === parts?.length - 1 ? path?.isFolder : i < parts.length - 1
        const currentPath = `${parentPath ? parentPath + '/' : ''}${part}`

        if (!currentLevel[part]) {
          currentLevel[part] = {
            label: part,
            isFolder: isFolder || false,
            children: {},
            path: currentPath,
            parentPath: parentPath,
            ...(isFolder
              ? { defaultOpen: path?.displayFile ? false : true }
              : {
                  ...(path?.displayFile
                    ? { displayFile: path?.displayFile }
                    : {}),
                  content: path?.content || '',
                  candidate: {
                    allowView:
                      path?.candidate?.allowView == null
                        ? true
                        : path?.candidate?.allowView,
                    allowEdit:
                      path?.candidate?.allowEdit == null
                        ? true
                        : path?.candidate?.allowEdit
                  }
                })
          }
        }

        currentLevel = currentLevel[part].children
        parentPath = currentPath // Update parent path for the next iteration
      }
    }
  })

  return tree
}

export const recreateTreeForDBTChallenge = (
  currentTree,
  paths,
  language = 'dbt'
) => {
  let defaultData = EDITOR_DEFAULT_LANGUAGE_FILES?.[language] || []

  const onlyFiles = getFilesFromFolderItem({
    children: currentTree,
    isFolder: true
  })

  const allFilesPath = [...defaultData, ...onlyFiles, ...paths]

  const newTree = buildFolderAndFilesStructure(allFilesPath)
  return newTree
}

/**
 *
 * @param {object} tree
 * @param {string} path
 * @param {string} label
 * @returns
 */
export const updateTree = (tree, path, parentPath, label) => {
  const key = path?.split('/')?.slice(-1)?.[0] || ''
  const splitPath = path
    ? path?.split('/')?.slice(0, -1)
    : parentPath
      ? parentPath.split('/')
      : []
  const splitJoinPath = splitPath.join('/')
  const newPath = `${splitJoinPath ? `${splitJoinPath}/` : ''}${label}`
  const trimmedValue = label?.trim()

  let currentLevel = { ...tree }

  if (key === label && path) {
    return currentLevel
  } else if (!trimmedValue && !path) {
    delete currentLevel['']
    return currentLevel
  } else {
    const updatedNode = {
      ...currentLevel[key],
      label,
      parentPath: splitJoinPath,
      path: newPath
    }

    const updateChildPaths = (node, parentPath) => {
      if (node.children) {
        const updatedChildren = {}
        Object.values(node.children).forEach((child) => {
          const childPath = `${parentPath}/${child.label}`
          const updatedChild = {
            ...child,
            path: childPath,
            parentPath
          }
          updateChildPaths(updatedChild, childPath)
          updatedChildren[updatedChild.label] = updatedChild
        })
        node.children = updatedChildren
      }
    }

    updateChildPaths(updatedNode, newPath)

    const checkAvailableLabel = (label, node) => {
      if (currentLevel[label] !== undefined) {
        const newLabel = node?.isFolder
          ? `${label}(1)`
          : `${label.split('.')?.[0]}(1)${
              label.split('.')?.[1] ? `.${label.split('.')?.[1]}` : ''
            }`

        return checkAvailableLabel(newLabel, node)
      } else {
        return label
      }
    }

    const finalLabel = checkAvailableLabel(label, updatedNode)

    if (label !== finalLabel) {
      return updateTree(tree, path, parentPath, finalLabel)
    } else {
      currentLevel[label] = updatedNode
      delete currentLevel[key]
      return !updatedNode.isFolder
        ? { updatedNode, tree: currentLevel }
        : currentLevel
    }
  }
}

/**
 *
 * @param {object} tree
 * @param {string} path
 * @param {string} content
 * @returns update tree
 */
export const getUpdatedTreeFromContent = (tree, path, content) => {
  const split = path?.split('/') || []
  const label = split?.slice(-1)?.[0]
  const splitPath = split?.slice(0, -1) || []
  const newTree = { ...tree }

  let currentLevel = newTree
  for (let i = 0; i < splitPath.length; i++) {
    if (currentLevel[splitPath[i]]) {
      const { children, ...rest } = currentLevel[splitPath[i]]
      currentLevel[splitPath[i]] = { ...rest, children: { ...children } } // Create a new object for 'children'
      currentLevel = currentLevel[splitPath[i]].children
    }
  }

  if (currentLevel?.[label]) {
    currentLevel[label] = { ...currentLevel[label], content }
  }

  return newTree
}

/**
 * Remove Editor Changes from session storage
 */
export const removeEditorChangesFromSession = () => {
  sessionStorage.removeItem('editor-changes')
}

/**
 *
 * @param {object} changes
 */
export const setEditorChangesInSession = (changes) => {
  sessionStorage.setItem('editor-changes', JSON.stringify(changes))
}

/**
 *
 * @param {string} id
 */
export const setEditorAttemptId = (id) => {
  const editorChanges = getEditorChangesFromSession(false) || {}

  setEditorChangesInSession({
    ...editorChanges,
    attemptId: id
  })
}

/**
 *
 * @param {string} challengeId
 * @param {string} programmingChallengeId
 * @param {object} files
 */
export const updateCodeFilesForCandidate = (
  challengeId,
  programmingChallengeId,
  files
) => {
  const editorChanges = getEditorChangesFromSession(false) || {}
  setEditorChangesInSession({
    ...editorChanges,
    [challengeId]: {
      ...(editorChanges?.[challengeId] || {}),
      codeFiles: {
        ...(editorChanges?.[challengeId]?.codeFiles || {}),
        [programmingChallengeId]: files
      }
    }
  })
}

/**
 *
 * @param {number} index
 */
export const setEditorCurrentlySelectedChallengeIndex = (index) => {
  const editorChanges = getEditorChangesFromSession(false) || {}

  setEditorChangesInSession({
    ...editorChanges,
    currentlySelectedChallengeIndex: index
  })
}

/**
 *
 * @param {string} challengeId
 * @param {string} prgrammingChallengeId
 */
export const setEditorCurrentlySelectedProgrammingChallengeId = (
  challengeId,
  prgrammingChallengeId
) => {
  const editorChanges = getEditorChangesFromSession(false) || {}

  setEditorChangesInSession({
    ...editorChanges,
    [challengeId]: {
      ...(editorChanges?.[challengeId] || {}),
      currentlySelectedProgrammingChallengeId: prgrammingChallengeId
    }
  })
}

/**
 *
 * @param {string} challengeId
 * @param {string} sqlContent
 */
export const setEditorSQLContent = (challengeId, sqlContent) => {
  const editorChanges = getEditorChangesFromSession(false) || {}

  setEditorChangesInSession({
    ...editorChanges,
    [challengeId]: sqlContent
  })
}

/**
 *
 * @returns Editor Changes Value from Storage
 */
export const getEditorChangesFromSession = (needToCheck = true) => {
  const changes = JSON.parse(sessionStorage.getItem('editor-changes') || null)
  if (!needToCheck) {
    return changes
  } else if (window.location.href === changes?.location) {
    return changes
  } else {
    removeEditorChangesFromSession()
    return null
  }
}

/**
 *
 * @param {object} folderItem
 * @returns
 */
export const getFilesFromFolderItem = (folderItem) => {
  const files = []

  const parseFiles = (node) => {
    const hasLength = Object.values(node?.children || {}).length > 0

    if (hasLength) {
      Object.values(node?.children).forEach((child) => {
        if (!child?.isFolder && !child.displayFile) {
          files.push(child)
        }
        parseFiles(child)
      })
    }
  }

  parseFiles(folderItem)

  return files
}

/**
 *
 * @param {object} challenges
 * @returns
 */
export const createStageChallengeDetailsObj = (
  challenges,
  id,
  isProgrammingChallenge,
  preferLanguage = null
) => {
  const attemptId = id ? parseInt(id) : null
  const obj = {}
  const editorChanges = JSON.parse(
    sessionStorage.getItem('editor-changes') || null
  )
  challenges.forEach((challenge) => {
    let preferedLanguage = null

    if (attemptId === editorChanges?.attemptId && isProgrammingChallenge) {
      if (
        editorChanges?.[challenge?.id] &&
        editorChanges?.[challenge?.id]?.currentlySelectedProgrammingChallengeId
      ) {
        preferedLanguage =
          editorChanges?.[challenge?.id]
            ?.currentlySelectedProgrammingChallengeId
      } else if (preferLanguage) {
        preferedLanguage =
          challenge?.programmingChallenges?.find(
            (pg) => pg?.language === preferLanguage
          )?.id || null
      }
    } else if (preferLanguage) {
      preferedLanguage =
        challenge?.programmingChallenges?.find(
          (pg) => pg?.language === preferLanguage
        )?.id || null
    }

    obj[challenge.id] = {
      programmingChallengeId: challenge?.id,
      preferedLanguage,
      files: {}
    }
  })
  return obj
}

/**
 *
 * @param {Object} streams Array of object of consumer streams
 * @returns Grouped the consumer stream by socketId
 */
export const groupNewConsumerStream = (streams, requiredObject = false) => {
  if (streams?.length > 0) {
    const updatedStream = streams.reduce((prev, curr) => {
      if (prev?.[curr.email]) {
        prev[curr.email] = {
          ...(prev?.[curr.email] || {}),
          socketId: curr.socketId,
          hostname: curr?.hostname,
          worker: curr?.worker,
          roomName: curr?.roomName,
          [curr?.type]: {
            track: curr?.track,
            producerId: curr?.producerId,
            paused: curr?.paused
          }
        }
      } else {
        prev[curr.email] = {
          name: curr?.name,
          role: curr?.role,
          email: curr?.email,
          authRole: curr?.role,
          socketId: curr.socketId,
          hostname: curr?.hostname,
          worker: curr?.worker,
          roomName: curr?.roomName,
          [curr?.type]: {
            track: curr?.track,
            producerId: curr?.producerId,
            paused: curr?.paused
          }
        }
      }
      return prev
    }, {})

    return requiredObject
      ? updatedStream
      : Object.values(updatedStream)?.sort((a, b) =>
          a?.name?.localeCompare(b?.name)
        )
  } else {
    return requiredObject ? {} : []
  }
}

/**
 *
 * @param {string} url
 * @param {string} params
 * @returns
 */
export const reverseEngineerURL = (url, params) => {
  // Get an array of parameter keys
  const paramKeys = Object.keys(params)

  // Replace each parameter value in the URL with a placeholder
  let reversedURL = url
  paramKeys.forEach((paramKey) => {
    reversedURL = reversedURL.replace(`/${params[paramKey]}`, `/:${paramKey}`)
  })

  return reversedURL
}

/**
 *
 * @param {number} total
 * @param {number} numChallenges
 * @returns
 */
export const distributeWeights = (total, numChallenges) => {
  const initialWeight = total / numChallenges
  const roundedWeight = Math.round(initialWeight * 100) / 100

  let weights = Array(numChallenges).fill(roundedWeight)
  let currentSum = weights.reduce((sum, weight) => sum + weight, 0)
  let difference = Math.round((total - currentSum) * 100) / 100

  let i = 0
  while (difference !== 0) {
    if (difference > 0) {
      weights[i] = Math.round((weights[i] + 0.01) * 100) / 100
      difference = Math.round((difference - 0.01) * 100) / 100
    } else if (difference < 0) {
      weights[i] = Math.round((weights[i] - 0.01) * 100) / 100
      difference = Math.round((difference + 0.01) * 100) / 100
    }
    i = (i + 1) % numChallenges
  }

  return weights
}

/**
 *
 * @param {Function} handleConfirm
 * @param {Function} handleCancel
 */
export const showSweetAlertForMultipleEditorOpen = (
  handleConfirm,
  handleCancel
) => {
  Swal.fire({
    icon: 'info',
    text: 'This tab is already open. Are you sure you want to make this as active tab.',
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    showCancelButton: true
  }).then(async (result) => {
    if (result.isConfirmed) {
      handleConfirm()
    } else {
      handleCancel()
    }
  })
}

/**
 *
 * @param {Function} handleConfirm
 * @param {Function} handleCancel
 */
export const showConfirmationAlertDialog = (
  text,
  handleConfirm,
  handleCancel
) => {
  Swal.fire({
    text,
    icon: 'info',
    confirmButtonText: 'Confirm',
    cancelButtonText: 'Cancel',
    showCancelButton: true
  }).then(async (result) => {
    if (result.isConfirmed) {
      handleConfirm()
    } else {
      handleCancel()
    }
  })
}

/**
 *
 * @param {string} src
 * @returns
 */
export const loadScript = (src) => {
  return new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = src
    script.onload = () => {
      resolve(true)
    }
    script.onerror = () => {
      resolve(false)
    }
    document.body.appendChild(script)
  })
}

/**
 *
 * @param {boolean} hidden
 */
export const mainContainerOverflow = (hidden = true) => {
  const mainContainer = document.querySelector('main')
  if (mainContainer) {
    mainContainer.style.overflowY = hidden ? 'hidden' : ''
  }
}

/**
 *
 * @param {string} text
 */
export const copyToClipboard = async (
  text,
  message = 'Copied to the clipboard'
) => {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(message)
  } catch (err) {
    toast.error('Failed to copy text to clipboard', err)
  }
}
/**
 *
 * @param {date} date
 * @param {string} format
 * @returns  formatted date-time string
 */
export const formatDateTime = (date, showDate = false, showTime = false) => {
  return date
    ? moment(date).format(
        showDate ? 'DD-MMM-YYYY' : showTime ? 'HH:mm' : 'DD-MMM-YYYY HH:mm'
      )
    : ''
}

/**
 *
 * @param {date string} d1
 * @param {date string} d2
 * @returns
 */
export const sortDateWithNullValues = (d1, d2) => {
  const date1 = d1 ? new Date(d1) : new Date(-8640000000000000)
  const date2 = d2 ? new Date(d2) : new Date(-8640000000000000)

  if (isNaN(date1.getTime())) date1.setTime(-8640000000000000)
  if (isNaN(date2.getTime())) date2.setTime(-8640000000000000)

  return date1 - date2
}

/**
 *
 * @param {prerequisiteResult object} a
 * @param {prerequisiteResult object} b
 * @returns number
 */
export const sortPrequisiteStatus = (a, b) => {
  const getSortValue = (result) => {
    if (result?.skipPrerequisite) {
      return 1
    }
    if (result?.prerequisiteApproved === null) {
      return 2
    }
    if (result?.prerequisiteApproved) {
      return 3
    }
    return 4
  }

  const sortValueA = getSortValue(a)
  const sortValueB = getSortValue(b)

  return sortValueA - sortValueB
}

export const getPrerequisiteLabelAndColor = (row) => {
  const { prerequisiteResult = null } = row || {}
  const { skipPrerequisite, prerequisiteApproved } = prerequisiteResult || {}

  const label = skipPrerequisite
    ? 'Skipped'
    : prerequisiteApproved === null
      ? 'Pending'
      : prerequisiteApproved === true
        ? 'Passed'
        : prerequisiteApproved === false
          ? 'Failed'
          : '-'

  const labelColor = skipPrerequisite
    ? 'var(--primary-main)'
    : prerequisiteApproved === null
      ? 'var(--warning-main)'
      : prerequisiteApproved === true
        ? 'var(--success-main)'
        : prerequisiteApproved === false
          ? 'var(--error-main)'
          : 'var(--text-primary)'

  return { label, labelColor }
}

/**
 *
 * @param {string} dateStr
 * @returns converts recording filename date to proper ISO string
 */
export const convertToISO = (dateStr) => {
  // Replace dashes in the time section with colons
  return dateStr.replace(/-(\d{2})-(\d{2})\./, ':$1:$2.')
}

/**
 *
 * @param {Object} data
 * @returns
 */
export const formatProducerObj = (data) => {
  let obj = {}
  const details = getUserDetails()
  Object.keys(data).forEach((h) => {
    Object.keys(data?.[h]).forEach((w) => {
      Object.keys(data?.[h]?.[w]).forEach((r) => {
        data?.[h]?.[w]?.[r]?.peers?.forEach((p) => {
          if (p?.email !== details?.sub) {
            obj[`${p?.socketId}-${p?.email}`] = {
              ...p,
              hostname: h,
              worker: w,
              roomName: r
            }
          }
        })
      })
    })
  })
  const returnValues = Object.values(obj)
  return returnValues.sort((a, b) => a?.name?.localeCompare(b?.name))
}

/**
 *
 * @param {string} subDomainLabel
 * @param {string} contestUri
 * @returns url for redirection
 */

export const getLinkFromSubDomainLabel = (
  subDomainLabel,
  contestUri = null
) => {
  const protocol = window.location.protocol
  const linkToAppend = contestUri ? `/c/${contestUri}/stages` : ''
  const port = window.location.port ? `:${window.location.port}` : ''

  return `${protocol}//${subDomainLabel}.coderscout.io${port}${linkToAppend}`
}

/**
 *
 * @param {String} runCodeId
 * @returns
 */
export const createRunCodeTimer = (
  runCodeId,
  key,
  message,
  timerMilliSeconds = 60000
) => {
  clearRunCodeTimer()
  const timer = setTimeout(() => {
    toast.error(message)
    if (window.eventEmitter) {
      window.eventEmitter.emit(key)
    }
  }, timerMilliSeconds)

  window.RUN_CODE_CONFIG = {
    key,
    timer,
    message,
    runCodeId
  }
}

/**
 *
 */
export const clearRunCodeTimer = () => {
  if (window?.RUN_CODE_CONFIG?.timer) {
    clearTimeout(window?.RUN_CODE_CONFIG?.timer)
    window.RUN_CODE_CONFIG = undefined
  }
}

/**
 * This will be called when run code is clicked and internet disconnect
 */
export const showRunCodeErrorForDisconnect = () => {
  if (window?.RUN_CODE_CONFIG?.timer) {
    const data = window.RUN_CODE_CONFIG
    clearRunCodeTimer()
    createRunCodeTimer(data?.runCodeId, data?.key, data?.message, 0)
  }
}

/**
 *
 * @returns client browser name
 */
export const detectClientBrowserName = () => {
  const userAgent = window.navigator.userAgent
  let browserName, browserVersion

  if (
    window.navigator.brave &&
    typeof window.navigator.brave.isBrave === 'function'
  ) {
    browserName = 'Brave'
    const chromeMatch = userAgent.match(/Chrome\/([\d.]+)/)
    browserVersion = chromeMatch ? chromeMatch[1] : 'unknown'
  } else if (userAgent.indexOf('Edg') > -1) {
    browserName = 'Microsoft Edge'
    const edgeMatch = userAgent.match(/Edg\/([\d.]+)/)
    browserVersion = edgeMatch ? edgeMatch[1] : 'unknown'
  } else if (userAgent.indexOf('Chrome') > -1) {
    browserName = 'Chrome'
    const chromeMatch = userAgent.match(/Chrome\/([\d.]+)/)
    browserVersion = chromeMatch ? chromeMatch[1] : 'unknown'
  } else if (userAgent.indexOf('Firefox') > -1) {
    browserName = 'Firefox'
    const firefoxMatch = userAgent.match(/Firefox\/([\d.]+)/)
    browserVersion = firefoxMatch ? firefoxMatch[1] : 'unknown'
  } else if (
    userAgent.indexOf('Safari') > -1 &&
    userAgent.indexOf('Chrome') === -1
  ) {
    browserName = 'Safari'
    const safariMatch = userAgent.match(/Version\/([\d.]+)/)
    browserVersion = safariMatch ? safariMatch[1] : 'unknown'
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browserName = 'Opera'
    const operaMatch = userAgent.match(/(Opera|OPR)\/([\d.]+)/)
    browserVersion = operaMatch ? operaMatch[2] : 'unknown'
  } else if (
    userAgent.indexOf('Trident') > -1 ||
    userAgent.indexOf('MSIE') > -1
  ) {
    browserName = 'Internet Explorer'
    const versionMatch = userAgent.match(/(MSIE\s|rv:)([\d.]+)/)
    browserVersion = versionMatch ? versionMatch[2] : 'unknown'
  } else {
    return null
  }

  return browserName ? `${browserName} version: ${browserVersion}` : null
}

export const checkBrowserIsChromium = () => {
  const browserInfo = detectClientBrowserName()
  return (
    browserInfo?.includes('Chrome') ||
    browserInfo?.includes('Edge') ||
    browserInfo?.includes('Opera')
  )
}

export const tabSwitchBrowser = () => {
  const browserInfo = detectClientBrowserName()
  return (
    browserInfo?.includes('Chrome') ||
    browserInfo?.includes('Edge') ||
    browserInfo?.includes('Opera') ||
    browserInfo?.includes('Brave') ||
    browserInfo?.includes('Safari')
  )
}

/**
 *
 * @returns client os
 */
export const detectClientOS = () => {
  const appVersion = window.navigator.appVersion

  if (appVersion.indexOf('Win') > -1) {
    return 'Windows'
  } else if (appVersion.indexOf('Mac') > -1) {
    return 'MacOS'
  } else if (appVersion.indexOf('X11') > -1) {
    return 'UNIX'
  } else if (appVersion.indexOf('Linux') > -1) {
    return 'Linux'
  } else {
    return null
  }
}

/**
 *
 * @returns reutrn candidate specifications object
 */
export const getCandidateSpecifications = () => {
  const navigator = window.navigator

  const response = {
    platform: navigator?.platform || null,
    os: detectClientOS(),
    browser: detectClientBrowserName()
  }

  return response
}

export const getDateTypeData = () => {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  const thisWeekStart = new Date(today)
  thisWeekStart.setDate(today.getDate() - today.getDay())
  thisWeekStart.setHours(0, 0, 0, 0)

  const lastWeekStart = new Date(thisWeekStart)
  lastWeekStart.setDate(lastWeekStart.getDate() - 7)

  const lastWeekEnd = new Date(lastWeekStart)
  lastWeekEnd.setDate(lastWeekEnd.getDate() + 6)
  lastWeekEnd.setHours(23, 59, 59, 999)

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)

  const last7DaysStart = new Date(today)
  last7DaysStart.setDate(today.getDate() - 7)
  last7DaysStart.setHours(0, 0, 0, 0)

  return [
    {
      label: 'Today',
      value: 'today',
      startDate: new Date(today.setHours(0, 0, 0, 0)).toISOString(),
      endDate: new Date(today.setHours(23, 59, 59, 999)).toISOString()
    },
    {
      label: 'Yesterday',
      value: 'yesterday',
      startDate: new Date(yesterday.setHours(0, 0, 0, 0)).toISOString(),
      endDate: new Date(yesterday.setHours(23, 59, 59, 999)).toISOString()
    },
    {
      label: 'This Week',
      value: 'thisWeek',
      startDate: thisWeekStart.toISOString(),
      endDate: new Date(today.setHours(23, 59, 59, 999)).toISOString()
    },
    {
      label: 'Last Week',
      value: 'lastWeek',
      startDate: lastWeekStart.toISOString(),
      endDate: lastWeekEnd.toISOString()
    },
    {
      label: 'Last 7 Days',
      value: 'last7Days',
      startDate: last7DaysStart.toISOString(),
      endDate: new Date(today.setHours(23, 59, 59, 999)).toISOString()
    },
    {
      label: 'This Month',
      value: 'thisMonth',
      startDate: thisMonthStart.toISOString(),
      endDate: new Date(today.setHours(23, 59, 59, 999)).toISOString()
    },
    {
      label: 'Last Month',
      value: 'lastMonth',
      startDate: lastMonthStart.toISOString(),
      endDate: lastMonthEnd.toISOString()
    },
    {
      label: 'Custom',
      value: 'custom'
    }
  ]
}

/**
 *
 * @param {string} byteData
 * @returns
 */
export const convertBytesToBase64 = (byteData) => {
  return `data:image/png;base64,${byteData}`
}

/**
 *
 * @param {boolean} loadingValue
 */
export const setGetAudioVideoLoading = (loadingValue) => {
  if (window.eventEmitter) {
    window.eventEmitter.emit('getAudioVideoLoading', { loading: loadingValue })
  }
}

/**
 *
 * @param {Object} response
 */
export const showErrorToast = (response) => {
  const message = response?.message || ERROR_500
  const toastId = message?.toLowerCase()?.replaceAll(' ', '-')

  if (toast.isActive(toastId)) {
    toast.update(toastId, { autoClose: 5000 })
  } else {
    toast.error(message, {
      toastId: toastId,
      autoClose: 5000
    })
  }
}

/**
 *
 * @param {number} amount
 * @param {string} currency
 * @returns
 */
export const formatCurrency = (amount, currency, withSymbol = true) => {
  const localeFormatter = currencyLocaleMap[currency] || 'en-US'

  try {
    const hasFraction = amount % 1 !== 0

    const formatter = new Intl.NumberFormat(localeFormatter, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    })

    const formattedValue = formatter.format(amount)
    return withSymbol ? formattedValue : formattedValue.slice(1)
  } catch (error) {
    return withSymbol
      ? `${currencyMap?.[currency]} ${amount.toFixed(2)}`
      : `${amount.toFixed(2)}`
  }
}

export const needEventTimeout = (method) => {
  if (method === 'challenge-editor/run') {
    return {
      message: 'Challenge run timed out',
      key: 'runCodeFailed'
    }
  } else if (method === 'database/run-sql') {
    return {
      message: 'SQL run timed out',
      key: 'runSQLFailed'
    }
  } else if (method === 'database/init') {
    return {
      message: 'Metadata generate timed out',
      key: 'metadataGenerationFailed'
    }
  } else if (method === 'database/run-app-sql') {
    return {
      message: 'SQL run timed out',
      key: 'runAppSQLFailed'
    }
  } else if (method === 'database/reset-app-database') {
    return {
      message: 'Reset database timed out',
      key: 'resetDBFailed'
    }
  } else {
    return null
  }
}

export const closeWithConfirmation = (handler) => {
  Swal.fire({
    icon: 'info',
    text: 'Are you sure you want to close this window? Any unsaved changes will be lost.',
    confirmButtonText: 'Confirm',
    showCancelButton: true
  }).then((result) => {
    if (result?.isConfirmed) {
      handler()
    }
  })
}

/**
 *
 * @param {number} minutes
 * @returns Convert Mintues to Hrs in decimal Format
 */
export const convertMinutesToDecimalHours = (minutes) => {
  const hours = (minutes / 60).toFixed(2)
  return `${hours} hrs`
}

/**
 *
 * @param {number} totalMinutes
 * @returns Converts mins into hrs and mins
 */

export const convertMinutesToHoursAndMinutes = (
  inputTotalMinutes,
  needAmpersand = false
) => {
  const totalMinutes = !isNaN(inputTotalMinutes)
    ? parseInt(inputTotalMinutes)
    : inputTotalMinutes

  if (totalMinutes <= 0 || !totalMinutes) {
    return '0 min'
  }

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  const parts = []
  if (hours) {
    parts.push(`${hours} ${hours === 1 ? 'hr' : 'hrs'}`)
  }
  if (minutes) {
    parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`)
  }

  return parts?.length > 0 ? parts.join(needAmpersand ? ' & ' : ' ') : ''
}

export const getTreeNode = (tree = {}, path = '') => {
  if (path) {
    const pathParts = path.split('/')

    let currentNode = tree

    for (const part of pathParts) {
      if (currentNode.children && currentNode.children[part]) {
        currentNode = currentNode.children[part]
      } else if (currentNode[part]) {
        currentNode = currentNode[part]
      } else {
        return null
      }
    }

    return currentNode
  } else {
    return null
  }
}

export const requestFullScreen = async () => {
  const doc = window?.document?.documentElement
  try {
    await doc.requestFullscreen({ navigationUI: 'hide' })
    return true
  } catch (error) {
    return false
  }
}

export const normalizeText = (text) => text.replace(/\r\n/g, '\n').trim()

/**
 *
 * @returns LIST OF ALL TIMEZONES
 */
export const getTimeZones = () => {
  const timezones = moment.tz.names()
  return (
    timezones || [
      // fallback list
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Asia/Kolkata',
      'Asia/Dubai',
      'America/Toronto',
      'America/Vancouver',
      'Australia/Sydney',
      'Australia/Melbourne',
      'Australia/Perth',
      'UTC'
    ]
  )
}

/**
 *
 * @returns current timezone
 */
export const getCurrentTimeZone = () => {
  return moment.tz.guess() === 'Asia/Calcutta'
    ? 'Asia/Kolkata'
    : moment.tz.guess()
}

/**
 *
 * @param {boolean} isQuestion
 * @returns
 */
export const publishConfirmation = async (isQuestion = false, isDB = false) => {
  const result = await Swal.fire({
    icon: 'warning',
    html: `Are you sure you want to publish this ${
      isQuestion ? 'question' : isDB ? 'database' : 'challenge'
    }?<br>Once published, it cannot be unpublished.`,
    showCancelButton: true,
    confirmButtonText: 'Yes, publish'
  })

  return result.isConfirmed
}

export const insertCharacterAtPosition = (
  inputText,
  targetLine,
  targetCharacter,
  charToInsert
) => {
  let lines = inputText.split('\n')

  if (
    targetLine <= 0 ||
    targetLine > lines.length ||
    targetCharacter < 0 ||
    targetCharacter > lines[targetLine - 1].length
  ) {
    return inputText
  }

  lines[targetLine - 1] =
    lines[targetLine - 1].slice(0, targetCharacter) +
    charToInsert +
    lines[targetLine - 1].slice(targetCharacter)

  return lines.join('\n')
}

export const openBlobInNewTab = (data, mimeType = '') => {
  const blob = mimeType
    ? new Blob([data], { type: mimeType })
    : new Blob([data])
  const url = window.URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => window.URL.revokeObjectURL(url), 10000)
}

export const downloadPDFBlob = (data, fileName, mimeType = '') => {
  const blob = mimeType
    ? new Blob([data], { type: mimeType })
    : new Blob([data])
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const ellipsisMiddle = (text, startChars = 10, endChars = 8) => {
  if (text?.length <= startChars + endChars + 3) return text
  return `${text?.slice(0, startChars)}...${text.slice(-endChars)}`
}

export const swalAlert = async (options) => {
  const {
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    icon = 'info',
    ishtml = false,
    ...rest
  } = options

  return await Swal.fire({
    icon,
    title,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    showCancelButton: true,
    ...(ishtml ? { html: message } : { text: message }),
    ...rest
  })
}
export const exportToCsv = (data, selectedColumns = null) => {
  if (!data?.length) return

  // derive keys and labels
  const columns = selectedColumns?.length
    ? selectedColumns
    : Object.keys(data[0]).map((key) => ({ key, label: key }))

  const csvRows = []

  // header row with labels
  csvRows.push(columns.map((col) => `"${col.label}"`).join(','))

  // data rows
  for (const row of data) {
    const values = columns.map((col) => row[col.key])
    csvRows.push(values.join(','))
  }

  downloadPDFBlob(csvRows?.join('\n'), 'data.csv', { type: 'text/csv' })
}

export const getAccurateDateDiff = (from, until) => {
  let years = until.getFullYear() - from.getFullYear()
  let months = until.getMonth() - from.getMonth()
  let days = until.getDate() - from.getDate()

  if (days < 0) {
    months -= 1
    const prevMonth = new Date(until.getFullYear(), until.getMonth(), 0)
    days += prevMonth.getDate()
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  return { years, months, days }
}

export const hasMatchingQuestionSet = (
  newQuestionSet,
  sourceData,
  options = { excludedSetId: null, excludeSetIndex: null }
) => {
  // id or index to exclude
  const { excludedSetId, excludeSetIndex } = options

  // rest of the data
  const filteredQuestionSets =
    typeof excludeSetIndex === 'number'
      ? sourceData?.filter((_, i) => i !== excludeSetIndex)
      : excludedSetId
        ? sourceData?.filter((set) => set?.id !== excludedSetId)
        : sourceData

  // helper function
  const hasCommonTopic = (topics1 = [], topics2 = []) => {
    return topics1.some((t) => topics2.includes(t))
  }

  return filteredQuestionSets?.some((existingSet) => {
    // current set
    const {
      difficulty: currentSetDifficulty = [],
      topicIds: newTopicIds = []
    } = newQuestionSet || {}

    // existing sets
    const {
      difficulty: existingSetDifficulty = [],
      topicIds: existingTopicIds = []
    } = existingSet || {}

    const hasCommonTopicCheck = hasCommonTopic(newTopicIds, existingTopicIds)

    const hasCommonDifficulty = currentSetDifficulty === existingSetDifficulty

    return hasCommonTopicCheck && hasCommonDifficulty
  })
}

export const getCandidatePipelineMessage = (
  status,
  startTime = null,
  stageName = '',
  strictPrequisite = false,
  prerequisiteResult = null,
  skipped = false,
  stageType,
  startByWindow = null,
  deadline = null,
  stageDuration = 0,
  isFirstStage = true,
  isAssessment = false
) => {
  const isInterviewOrTalk =
    stageType === CHALLENGE_TYPES.INTERVIEW ||
    stageType === CHALLENGE_TYPES.PLACEMENT_TALK ||
    stageType === CHALLENGE_TYPES.AI_SCREENING

  const isVideoResponse = stageType === CHALLENGE_TYPES.VIDEO_RESPONSE

  const STATUS_OPTIONS = {
    NOT_STARTED: 'NOT_STARTED',
    REJECTED: 'REJECTED',
    QUALIFIED: 'QUALIFIED',
    SUBMITTED: 'SUBMITTED',
    IN_PROGRESS: 'IN_PROGRESS',
    SCHEDULED: 'SCHEDULED',
    COMPLETED: 'COMPLETED',
    DISQUALIFIED: 'DISQUALIFIED',
    READY_TO_START: 'READY_TO_START',
    NO_SHOW: 'NO_SHOW'
  }

  switch (status) {
    case STATUS_OPTIONS.READY_TO_START: {
      const base = {
        label: 'Ready to Start',
        color: 'var(--success-main)',
        icon: 'weui:done2-filled'
      }

      const timeMessage =
        stageDuration &&
        stageType !== CHALLENGE_TYPES.INTERVIEW &&
        stageType !== CHALLENGE_TYPES.PLACEMENT_TALK &&
        isAssessment
          ? `\n\n Make sure you have ${convertMinutesToHoursAndMinutes(
              stageDuration,
              true
            )} of continuous time to complete the stage before you click ${
              isInterviewOrTalk ? 'Join' : 'Start'
            } button.`
          : ''

      if (isVideoResponse) {
        return {
          ...base,
          message: `The ${
            isFirstStage ? 'current' : 'next'
          } stage is ready for you to start. \n\n Please ensure you complete the current stage before ${formatDateTime(
            new Date(deadline),
            false,
            true
          )}, ${formatDateTime(
            new Date(deadline),
            true,
            false
          )} .${timeMessage}\n\n Click on ${
            isInterviewOrTalk ? 'Join' : 'Start'
          } to get started.`
        }
      }

      if (!isAssessment && !Boolean(startByWindow)) {
        return {
          ...base,
          message: `The ${
            isFirstStage ? 'current' : 'next'
          } stage is ready for you to start.${timeMessage}\n\n Click on ${
            isInterviewOrTalk ? 'Join' : 'Start'
          } immediately. `
        }
      }

      if (startByWindow) {
        return {
          ...base,
          message: `The ${
            isFirstStage ? 'current' : 'next'
          } stage is ready for you to start. \n\n Please make sure to ${
            isInterviewOrTalk ? 'join' : 'start'
          } before ${formatDateTime(
            new Date(startByWindow),
            false,
            true
          )}, ${formatDateTime(
            new Date(startByWindow),
            true,
            true
          )} .${timeMessage}\n\n Click on ${
            isInterviewOrTalk ? 'Join' : 'Start'
          } to get started.`
        }
      } else {
        return {
          ...base,
          message: `The ${
            isFirstStage ? 'current' : 'next'
          } stage is ready for you to start.${timeMessage}\n\n Click on ${
            isInterviewOrTalk ? 'Join' : 'Start'
          } to get started.`
        }
      }
    }

    case STATUS_OPTIONS.NOT_STARTED: {
      const base = {
        label: 'Not Started',
        color: 'var(--text-secondary)',
        icon: 'mingcute:lock-line'
      }

      if (strictPrequisite && !prerequisiteResult && !skipped) {
        return {
          ...base,
          message: `Your assessment hasn’t been scheduled yet. \n\n Complete the mandatory system checks as soon as possible to avoid any issues.`
        }
      } else {
        return {
          ...base,
          message:
            'Your assessment hasn’t been scheduled yet. Please check back later for further updates.'
        }
      }
    }

    case STATUS_OPTIONS.REJECTED:
      return {
        label: 'Not Selected',
        color: 'var(--error-main)',
        icon: 'solar:user-cross-line-duotone',
        message: `We appreciate you taking the time to apply. After careful consideration, we've determined that your skills don't align with the specific requirements for this position.`
      }

    case STATUS_OPTIONS.DISQUALIFIED:
      return {
        label: 'Disqualified',
        color: 'var(--error-main)',
        icon: 'solar:user-cross-line-duotone',
        message: `Due to a violation of assessment protocols, you have been disqualified from the selection process.`
      }

    case STATUS_OPTIONS.QUALIFIED:
      return {
        label: 'Qualified',
        color: 'var(--success-main)',
        icon: 'fluent-mdl2:completed-solid',
        message: isInterviewOrTalk
          ? `Please wait for further instructions regarding upcoming steps.`
          : `Congratulations! You have qualified for the next stage. Please check back later for further instructions regarding next steps.`
      }

    case STATUS_OPTIONS.SUBMITTED:
      return {
        label: 'Submitted',
        color: 'var(--info-main)',
        icon: 'prime:check-circle',
        message: isInterviewOrTalk
          ? 'Your current round has ended. Please check back later for further instructions regarding next steps.'
          : 'Your response has been submitted successfully. The evaluation process is underway. Please check back later for further instructions regarding next steps.'
      }

    case STATUS_OPTIONS.IN_PROGRESS:
      return {
        label: 'In Progress',
        color: 'var(--warning-main)',
        icon: 'mdi:progress-star',
        message: isInterviewOrTalk
          ? `Your assessment is currently in progress. Please join.`
          : deadline
            ? `Your assessment is currently in progress. \n\n Please ensure you complete the current stage before ${formatDateTime(
                new Date(deadline),
                false,
                true
              )}, ${formatDateTime(
                new Date(deadline),
                true,
                false
              )} . All the best!`
            : `Your assessment is currently in progress. Please ensure you complete it. All the best!`
      }

    case STATUS_OPTIONS.SCHEDULED:
      return {
        label: 'Scheduled',
        color: 'var(--warning-main)',
        icon: 'mdi:calendar',
        message: `The ${
          isFirstStage ? 'current' : 'next'
        } stage of your assessment is scheduled to begin on ${formatDateTime(
          startTime,
          true
        )} at ${formatDateTime(new Date(startTime), false, true)} . ${
          strictPrequisite && !prerequisiteResult && !skipped
            ? '\n\n Complete the mandatory system checks as soon as possible to avoid any issues.'
            : ''
        }`
      }

    case STATUS_OPTIONS.COMPLETED:
      return {
        label: 'Completed',
        icon: 'weui:done2-filled',
        color: 'var(--success-main)',
        message: `Thank you for your participation. Please wait for further communication regarding your results.`
      }
    case STATUS_OPTIONS.NO_SHOW:
      return {
        label: isAssessment ? 'Missed Start Deadline' : 'No Show',
        icon: 'tdesign:user-blocked-filled',
        color: 'var(--alert-main)',
        message: isAssessment
          ? `You did not start this stage within the allowed time window. Please contact your recruiter for further steps.`
          : `You did not participate in this stage during the available time. This stage has now ended. Please contact your recruiter for further steps.`
      }
    default:
      return {
        label: 'Unknown',
        color: 'var(--error-dark)',
        message: 'Status not recognized.'
      }
  }
}

export const showDurationUpdatedModal = async (previous, time) => {
  const previousDate = new Date(previous)
  const currentDate = new Date(time)

  const diffInMs = currentDate.getTime() - previousDate.getTime()
  const diffInMins = Math.round(diffInMs / 60000)

  if (diffInMins > 0) {
    await swalAlert({
      message: `Your allotted time has been extended by ${diffInMins} minute${
        diffInMins !== 1 ? 's' : ''
      } by the supervisor.`,
      icon: 'info',
      title: 'Information',
      showCancelButton: false,
      allowOutsideClick: false
    })
  }
}

export const isCandidateSelectable = (candidate, assessment) => {
  return assessment
    ? (candidate?.showNextStage && candidate?.nextStageId) ||
        ((candidate?.status === stageStatus.FINISHED ||
          candidate?.status === stageStatus.NOT_STARTED) &&
          candidate?.canRejectCandidate)
    : candidate?.showCheckbox
}
export const getCurrentPageDataAndSelection = (
  // used in previousAttempts.jsx and stageLeaderbaord
  filteredData,
  filters,
  assessment,
  selectedCandidateKeys
) => {
  const si = filters?.page * filters?.pageSize
  const ei = si + filters?.pageSize
  const pageData = filteredData?.slice(si, ei) || []

  const eligible = []
  let someSelected = false
  let allSelected = true

  for (const candidate of pageData) {
    const isEligible = isCandidateSelectable(candidate, assessment)

    if (!isEligible) {
      continue
    }

    eligible.push(candidate)

    const isSelected = selectedCandidateKeys?.includes(String(candidate?.id))
    if (isSelected) {
      someSelected = true
    } else {
      allSelected = false
    }
  }

  return {
    currentPageData: pageData,
    currentPageEligible: eligible,
    currentPageAllSelected: eligible.length > 0 && allSelected,
    currentPageSomeSelected: eligible.length > 0 && !allSelected && someSelected
  }
}

export const formatQuestionPreview = (htmlOrMarkdown, maxLength = 100) => {
  if (!htmlOrMarkdown) return ''

  const cleanHTML = DOMPurify.sanitize(htmlOrMarkdown)

  let plainText = ''
  {
    const tempElement = document.createElement('div')
    tempElement.innerHTML = cleanHTML
    plainText = tempElement.textContent || tempElement.innerText || ''
  }

  return plainText.length > maxLength
    ? plainText.slice(0, maxLength).trim() + '…'
    : plainText
}

export const getImportFileTooltip = (
  type,
  isDevUser,
  topics = [],
  libraryOptions = []
) => {
  const commonHeaders = [
    'content',
    'content_type',
    'mark',
    'difficulty',
    'skill',
    'tags',
    'correct_answers',
    'option1'
  ]

  const jsonContentTypes = ['PLAIN_TEXT', 'MARKDOWN', 'HTML']
  const jsonDifficulties = ['EASY', 'MEDIUM', 'HARD']

  if (type === 'xlsx' || type === 'csv') {
    return (
      <div>
        <ul style={{ paddingLeft: '1.5rem', marginTop: '8px' }}>
          <li>The file should be in {type.toUpperCase()} format.</li>
          <li>Headers must be in the following order:</li>
          <ul style={{ paddingLeft: '1.5rem' }}>
            {commonHeaders.map((header) => (
              <li key={header}>{header}</li>
            ))}
            {isDevUser && (
              <li>
                feature_library: Library Option (e.g., [
                {libraryOptions?.map((item) => item?.library)?.join(', ')}])
              </li>
            )}
          </ul>
          <li>
            Ensure no headers are missing, and the data under each header is
            correctly populated.
          </li>
          <li>
            Accepted file extensions:{' '}
            <span
              style={{
                background: 'var(--common-white)',
                padding: '1px 3px',
                borderRadius: '2px'
              }}
            >
              .{type}
            </span>
          </li>
        </ul>
      </div>
    )
  }

  if (type === 'json') {
    return (
      <div>
        <ul style={{ paddingLeft: '1.5rem', marginTop: '8px' }}>
          <li>The file should be in JSON format.</li>
          <li>Ensure your JSON file follows this structure:</li>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li>
              <strong>content:</strong> The question text (string).
            </li>
            <li>
              <strong>mark:</strong> Marks allocated for the question (number).
            </li>
            <li>
              <strong>skill:</strong> One of the following:
              <ul style={{ paddingLeft: '1.5rem' }}>
                {topics?.map((topic) => (
                  <li key={topic?.id}>{topic?.name}</li>
                ))}
              </ul>
            </li>
            <li>
              <strong>contentType:</strong> One of:
              <ul style={{ paddingLeft: '1.5rem' }}>
                {jsonContentTypes.map((type) => (
                  <li key={type}>{type}</li>
                ))}
              </ul>
            </li>
            <li>
              <strong>difficulty:</strong> One of:
              <ul style={{ paddingLeft: '1.5rem' }}>
                {jsonDifficulties.map((diff) => (
                  <li key={diff}>{diff}</li>
                ))}
              </ul>
            </li>
            <li>
              <strong>options:</strong> A list of options, each with:
              <ul style={{ paddingLeft: '1.5rem' }}>
                <li>
                  <strong>id:</strong> Unique identifier (number).
                </li>
                <li>
                  <strong>content:</strong> The option text (string).
                </li>
                <li>
                  <strong>isCorrect:</strong> true or false.
                </li>
              </ul>
            </li>
            <li>
              <strong>tags:</strong> A list of relevant tags (e.g., ["java",
              "loops"]).
            </li>
            {isDevUser && (
              <li>
                <strong>featureLibrary:</strong> Library Option (e.g., [
                {libraryOptions?.map((item) => item?.library)?.join(', ')}])
              </li>
            )}
          </ul>
        </ul>
      </div>
    )
  }

  if (type === 'mettl') {
    return (
      <div>
        <ul style={{ paddingLeft: '1.5rem', marginTop: '8px' }}>
          <li>The file should be exported from Mettl.</li>
          <li>
            The file may contain multiple sheets: Checkbox, MCQ, and Short
            Answer questions.
          </li>
          <li>
            The questions from the Checkbox and MCQ sheets will be uploaded.
          </li>
          <li>
            Ensure no headers are missing, and the data under each header is
            correctly populated.
          </li>
          <li>
            Accepted file extensions:{' '}
            <span
              style={{
                background: 'var(--common-white)',
                padding: '1px 3px',
                borderRadius: '2px'
              }}
            >
              .xlsx
            </span>
            and{' '}
            <span
              style={{
                background: 'var(--common-white)',
                padding: '1px 3px',
                borderRadius: '2px'
              }}
            >
              .xls
            </span>
          </li>
        </ul>
      </div>
    )
  }

  return null
}

const internalDevicesPatterns = [
  'internal',
  'built-in',
  'macbook',
  'speakers',
  'default audio'
]

export const getAudioOutputDeviceIdFilter = (
  outputDevices,
  checkLocalOutput = true
) => {
  if (outputDevices?.length === 0) return null

  const localOutputId = getOutputAudioDeviceId()

  if (localOutputId && checkLocalOutput) {
    const outputDevice = outputDevices?.find(
      (item) => item?.deviceId === localOutputId
    )

    if (outputDevice) {
      return outputDevice?.deviceId
    }
  }

  const externalDevice = outputDevices?.filter(
    (item) =>
      !internalDevicesPatterns?.some((innerItem) =>
        item?.label?.toLowerCase()?.includes(innerItem)
      ) && !item?.label?.toLowerCase()?.includes('stereo')
  )

  const internalDevice = outputDevices?.filter((item) =>
    internalDevicesPatterns?.some((innerItem) =>
      item?.label?.toLowerCase()?.includes(innerItem)
    )
  )

  const result =
    externalDevice?.[0]?.deviceId ||
    internalDevice?.[0]?.deviceId ||
    outputDevices?.[0]?.deviceId ||
    null

  return result
}

export const getFlagConfig = (flag) => {
  switch (flag) {
    case ASSESSMENT_STAGE_STATUS.EMAIL_NOT_VERIFIED:
      return {
        icon: 'mdi:email-alert',
        tooltip: TOOLTIPS?.EMAIL_NOT_VERIFIED,
        color: 'var(--warning-main)',
        fontSize: '18px'
      }
    case ASSESSMENT_STAGE_STATUS.SYSTEM_CHECK_FAILED:
      return {
        icon: 'tabler:settings-cancel',
        tooltip: TOOLTIPS?.SYSTEM_CHECK_FAILED,
        color: 'var(--error-main)'
      }
    case ASSESSMENT_STAGE_STATUS.SYSTEM_CHECK_PENDING:
      return {
        icon: 'material-symbols:settings-alert-outline',
        tooltip: TOOLTIPS?.SYSTEM_CHECK_PENDING,
        color: 'var(--warning-main)'
      }
    default:
      return null
  }
}

export const getAISolutionIconFromLabel = (input) => {
  if (!input) {
    return AI_SOLUTION_ICONS.default
  }

  const str = input.toLowerCase()

  const aiMapping = [
    { keywords: ['gemini'], icon: AI_SOLUTION_ICONS.gemini },
    {
      keywords: ['chatgpt', 'gpt', 'chat', 'openai'],
      icon: AI_SOLUTION_ICONS.chatgpt
    },
    { keywords: ['claude', 'anthropic'], icon: AI_SOLUTION_ICONS.claude },
    { keywords: ['perplexity'], icon: AI_SOLUTION_ICONS.perplexity }
  ]

  for (const { keywords, icon } of aiMapping) {
    if (keywords?.some((keyword) => str?.includes(keyword))) {
      return icon
    }
  }

  return AI_SOLUTION_ICONS.default
}

// returns time left in {hour, minute, second} format
export function getTimeLeft(targetTime) {
  if (!targetTime) return null

  const target = new Date(targetTime)
  const now = new Date()
  const diff = target - now

  if (diff <= 0) {
    return { hour: 0, minute: 0, second: 0 }
  }

  const hour = Math.floor(diff / (1000 * 60 * 60))
  const minute = Math.floor((diff / (1000 * 60)) % 60)
  const second = Math.floor((diff / 1000) % 60)

  return { hour, minute, second }
}

export const showRemarkPopup = (mark) => {
  Swal.fire({
    html: `
                  ${
                    mark?.event?.eventDetails?.userName
                      ? `
                      <blockquote style="
                        padding: 8px 16px;
                        border-left: 5px solid var(--primary-main);
                        font-style: italic;
                        position: relative;
                        text-align: left;
                        width: 100%;
                        margin-bottom: 5px;">
                        ${mark?.event?.eventDetails?.userName} Remark
                      </blockquote>
                    `
                      : ''
                  }
              <h3 style="white-space: pre-line; margin:0; text-align:left;">
                ${mark?.event?.eventDetails?.remark}
              </h3>
                `
  })
}

export const RegisterSqlFormatter = (monaco) => {
  monaco.languages.registerDocumentFormattingEditProvider('sql', {
    provideDocumentFormattingEdits: (model) => {
      const formatted = format(model.getValue(), { language: 'sql' })
      return [
        {
          range: model.getFullModelRange(),
          text: formatted
        }
      ]
    }
  })
}

// returns formatted file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const getFileExtension = (fileName) => {
  return fileName?.split('.').pop().toLowerCase()
}

export const showResumeFilteringStage = () => {
  return true
  // const userDetails = getUserDetails()

  // if (!!userDetails?.tenantId) {
  //   const TENANT_ID = userDetails?.tenantId
  //   const VALENS_TENANT_ID = '482e5823-3e0f-48dd-a68d-c073488e923c'
  //   const RACKSPACE_TENANT_ID = '97ceccb5-f67a-4c1e-94c2-aa54807b6da6'
  //   return (
  //     ENVIRONMENT === 'DEV' ||
  //     (ENVIRONMENT === 'PRODUCTION' &&
  //       (TENANT_ID === VALENS_TENANT_ID || TENANT_ID === RACKSPACE_TENANT_ID))
  //   )
  // } else {
  //   return false
  // }
}

export const shouldRenderPlaybackIcon = (type, status) => {
  const excludedTypes = [
    CHALLENGE_TYPES.INTERVIEW,
    CHALLENGE_TYPES.PLACEMENT_TALK
  ]

  const excludedStatuses = [
    ASSESSMENT_STAGE_STATUS.NO_SHOW,
    ASSESSMENT_STAGE_STATUS.NOT_STARTED
  ]

  if (excludedTypes?.includes(type)) return false
  if (excludedStatuses?.includes(status)) return false

  const requiresCompletion = [
    CHALLENGE_TYPES.AI_SCREENING,
    CHALLENGE_TYPES.VIDEO_RESPONSE
  ]

  if (requiresCompletion.includes(type)) {
    return status !== ASSESSMENT_STAGE_STATUS.IN_PROGRESS
  }

  return true
}

// use only in data grid
export const commonSortComparator = (
  v1,
  v2,
  param1,
  type = SORT_TYPE.NUMBER
) => {
  const sortOrder = param1?.api?.state?.sorting?.sortModel?.[0]?.sort || 'asc'

  if (v1 == null && v2 == null) return 0
  if (v1 == null) return sortOrder === 'desc' ? -1 : 1
  if (v2 == null) return sortOrder === 'desc' ? 1 : -1

  return type === SORT_TYPE.DATE
    ? new Date(v1) - new Date(v2)
    : type === SORT_TYPE.STRING
      ? v1.toLowerCase().localeCompare(v2.toLowerCase())
      : v1 - v2
}

// for sorting data manually
export const commonClientSort = (
  v1,
  v2,
  sortOrder = 'asc',
  type = SORT_TYPE.NUMBER
) => {
  if (v1 == null && v2 == null) return 0
  if (v1 == null) return 1
  if (v2 == null) return -1

  let result = 0

  switch (type) {
    case SORT_TYPE.DATE:
      result = new Date(v1) - new Date(v2)
      break

    case SORT_TYPE.STRING:
      result = String(v1).toLowerCase().localeCompare(String(v2).toLowerCase())
      break

    default:
      result = Number(v1) - Number(v2)
  }

  return sortOrder === 'asc' ? result : -result
}

export const buildResumeRejectionMessage = (data) => {
  if (!data) return ''

  const reasons = []
  const parsedData = JSON.parse(data)

  // Already participated
  if (reasons.length === 0 && parsedData?.previousContest?.length > 0) {
    reasons.push('The candidate has already participated in previous contests.')
  }

  // Criteria failed
  if (parsedData?.criteriaFailed?.length > 0 && reasons.length === 0) {
    const operatorMap = {
      EQUALS: 'equal to',
      GREATER_THAN: 'greater than',
      LESS_THAN: 'less than',
      GREATER_THAN_OR_EQUAL: 'greater than or equal to',
      LESS_THAN_OR_EQUAL: 'less than or equal to'
    }

    const criteriaText = parsedData?.criteriaFailed
      .map(
        (c) =>
          `${c.label} must be ${operatorMap[c.criteriaOperator]} ${c.criteriaValue} (candidates's score is ${c.value})`
      )
      .join(', ')

    reasons.push(`Eligibility criteria not met: ${criteriaText}.`)
  }

  // Resume score threshold
  if (
    reasons.length === 0 &&
    parsedData?.resumeScore &&
    parsedData?.resumeScore.value < parsedData?.resumeScore.minimumScore
  ) {
    reasons.push(
      `Resume score is below the required minimum of ${parsedData?.resumeScore.minimumScore}.`
    )
  }

  // If no failures (edge case)
  if (reasons.length === 0) {
    return ''
  }

  return reasons.join(' ')
}
